import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X, ChevronRight } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';

interface FullScreenScenarioOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  step?: number;
  stepLabels?: string[];
}

export function FullScreenScenarioOverlay({
  open,
  onOpenChange,
  children,
  step = 1,
  stepLabels = ['Basic Info', 'Procedures & Timeline', 'Review & Share'],
}: FullScreenScenarioOverlayProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setVisible(true);
      // Use setTimeout to ensure the DOM has rendered with opacity-0 before transitioning
      const animTimer = setTimeout(() => setAnimating(true), 20);
      document.body.style.overflow = 'hidden';
      return () => {
        clearTimeout(animTimer);
        document.body.style.overflow = '';
      };
    } else {
      setAnimating(false);
      const timer = setTimeout(() => {
        setVisible(false);
        document.body.style.overflow = '';
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onOpenChange(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onOpenChange]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[10020] flex flex-col transition-opacity duration-200 ${
        animating ? 'opacity-100' : 'opacity-0'
      } ${ahpDark ? 'bg-civic-bg-dark' : isAhp ? 'bg-civic-cream' : 'bg-background'}`}
    >
      {/* Top bar */}
      <div
        className={`flex items-center justify-between px-4 sm:px-6 h-14 border-b flex-shrink-0 ${
          ahpDark
            ? 'bg-civic-card-dark border-civic-gold/20'
            : isAhp
            ? 'bg-white border-gray-200'
            : 'bg-card border-foreground/10'
        }`}
      >
        {/* Left: close */}
        <button
          onClick={() => onOpenChange(false)}
          className={`flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-1.5 transition-colors ${
            isAhp
              ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              : 'text-foreground/60 hover:text-foreground hover:bg-foreground/5'
          }`}
        >
          <X className="w-4 h-4" />
          <span className="hidden sm:inline">Close</span>
        </button>

        {/* Center: step breadcrumbs */}
        <div className="flex items-center gap-1 sm:gap-2">
          {stepLabels.map((label, idx) => {
            const stepNum = idx + 1;
            const isActive = stepNum === step;
            const isCompleted = stepNum < step;

            return (
              <div key={idx} className="flex items-center gap-1 sm:gap-2">
                {idx > 0 && (
                  <ChevronRight className={`w-3 h-3 ${
                    isAhp ? 'text-gray-300' : 'text-foreground/20'
                  }`} />
                )}
                <div className="flex items-center gap-1.5">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-all ${
                      isActive
                        ? isAhp
                          ? 'bg-civic-money text-white'
                          : 'bg-matrix-green text-black'
                        : isCompleted
                        ? isAhp
                          ? 'bg-civic-money/20 text-civic-money'
                          : 'bg-matrix-green/20 text-matrix-green'
                        : isAhp
                        ? 'bg-gray-100 text-gray-400'
                        : 'bg-foreground/5 text-foreground/30'
                    }`}
                  >
                    {stepNum}
                  </div>
                  <span
                    className={`hidden sm:inline text-xs font-medium ${
                      isActive
                        ? isAhp
                          ? 'text-gray-900'
                          : 'text-foreground'
                        : isAhp
                        ? 'text-gray-400'
                        : 'text-foreground/40'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right: spacer for balance */}
        <div className="w-20" />
      </div>

      {/* Content area */}
      <div
        ref={contentRef}
        className={`flex-1 overflow-y-auto transition-transform duration-200 ${
          animating ? 'translate-y-0' : 'translate-y-4'
        }`}
      >
        {children}
      </div>
    </div>
  );
}
